import createDynamicPageGroups from '../utils/createDynamicPageGroups'
import * as panelDefs from '@wix/wix-data-client-common/dist/esm/panels/panelDefs'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'
import {
  HELP_ARTICLES,
  isStudio,
  DYNAMIC_PAGE_TYPES,
  DYNAMIC_PAGE_VARIANT,
} from '@wix/wix-data-client-common'

export default async ({
  hostname,
  t,
  collectionsApi,
  routers,
  editorSdkProxy,
  editorType,
  experimentsManager,
}) => {
  const collections = await collectionsApi.getAll({
    includeHiddenAppCollections: true,
  })

  const isStudioEditor = isStudio(editorType)

  const [
    isPermittedToAddNewItem,
    isPermittedToEditStructure,
    isPermittedToManageItems,
  ] = await Promise.all([
    editorSdkProxy.document.permissions.isCustomPermissionsGranted({
      permissions: ['WIX_DATA.INSERT'],
    }),
    editorSdkProxy.document.permissions.isCustomPermissionsGranted({
      permissions: ['DOCUMENT.EDIT_STRUCTURE'],
    }),
    editorSdkProxy.document.permissions.isCustomPermissionsGranted({
      permissions: ['WIX_DATA.FIND'],
    }),
  ])

  const customGroups = await createDynamicPageGroups({
    routers,
    collections,
    t,
    editorSdkProxy,
    isStudioEditor,
    isPermittedToManageItems,
  })

  const applicationActions = [
    {
      title: t('App_Pages_Add_Button_Title'),
      event: 'openNewDynamicPagePanel',
      icon: 'page_dynamic_icon',
      type: 'add_page',
    },
    {
      title: t('App_Pages_Actions_Add_Preset'),
      event: 'addDynamicPageClicked',
      icon: 'page_dynamic_icon',
      type: 'add_page_secondary',
    },
    {
      title: t('App_Pages_Actions_Manage_Collections'),
      event: 'openContentManager',
      icon: 'settingsAction',
    },
  ]

  const manageContentAction =
    isPermittedToManageItems && isStudioEditor
      ? [
          {
            title: t('Dynamic_Pages_Page_Actions_Manage_Content'),
            event: 'manageContentClicked',
            icon: 'manage_content',
          },
        ]
      : []

  const defaultPageActions = [
    {
      title: t('Dynamic_Pages_Page_Actions_Page_Connections'),
      icon: 'pageData',
      event: 'openConnectionsListClicked',
    },
    'Pages_Actions_Page_Rename',
    ...[
      {
        title: t('Dynamic_Pages_Page_Actions_Add_New_Item'),
        event: 'addNewItemClicked',
        icon: 'add_new_item',
      },
    ].filter(() => isPermittedToAddNewItem),
    ...manageContentAction,
    ...[
      {
        title: t('Dynamic_Pages_Page_Actions_Duplicate'),
        event: 'duplicateDynamicPage',
        icon: 'duplicateAction',
      },
      {
        title: t('Dynamic_Pages_Page_Actions_Remove_Dynamic_Connections'),
        event: 'disconnectPageFromRouterClicked',
        icon: 'unlink_icon',
      },
      {
        title: t('Dynamic_Pages_Page_Actions_Page_Delete'),
        event: 'deleteDynamicPageClicked',
        icon: 'delete_icon',
      },
    ].filter(() => isPermittedToEditStructure),
  ]

  const addVariantAction =
    experimentsManager.enabled('specs.wixDataClient.dynamicPageVariants') &&
    isStudioEditor &&
    isPermittedToEditStructure
      ? [
          {
            title: t('Dynamic_Pages_Page_Actions_Add_Variant'),
            event: 'addDpVariantClicked',
          },
        ]
      : []

  const itemPageActions = [
    ...defaultPageActions.slice(0, defaultPageActions.length - 2),
    ...addVariantAction,
    ...defaultPageActions.slice(defaultPageActions.length - 2),
  ]

  return {
    applicationSettings: {
      default: {
        displayName: t('App_Pages_Display_Name'),
        helpId: '1e76f0cc-531a-45b3-bfe0-8907cf391fca',
        grouping: {
          customGroups,
          groupBy: 'custom',
        },
      },
    },
    applicationActions: {
      default: applicationActions,
    },
    pageActions: {
      default: defaultPageActions,
      [DYNAMIC_PAGE_TYPES.ITEM_PAGE]: itemPageActions,
      [DYNAMIC_PAGE_TYPES.INDEX_PAGE]: [...defaultPageActions],
      [DYNAMIC_PAGE_VARIANT]: [
        ...manageContentAction,
        {
          title: t('Dynamic_Pages_Page_Actions_Duplicate_Variant'),
          event: 'duplicateDpVariantClicked',
        },
        {
          title: t('Dynamic_Pages_Page_Actions_Edit_Variant'),
          event: 'editDpVariantClicked',
        },
      ].filter(
        () =>
          experimentsManager.enabled(
            'specs.wixDataClient.dynamicPageVariants',
          ) && isStudioEditor,
      ),
    },
    pageSettings: {
      default: [
        {
          type: 'page_info',
          title: t('Dynamic_Pages_Pages_Info_Tab_Title'),
          url: await getPanelUrl({
            hostname,
            editorSdkProxy,
            relativeUrl: panelDefs.dynamicPageInfoPanelV2.url,
          }),
          helpId: 'e200b3b9-2045-4551-86d9-d9d74e90bd53',
        },
        {
          type: 'layout',
          title: t('Pages_Layouts_Tab_Title'),
          helpId: 'd985e594-b73a-46ea-adb7-044ecc3c1acc',
        },
        {
          type: 'permissions',
          title: t('Pages_Permissions_Tab_Title'),
          helpId: '6c369b66-9450-45a1-aedb-f347459f9e97',
        },
        {
          type: 'seo',
          title: t(
            experimentsManager.enabled('specs.wixDataClient.SEOSettings')
              ? 'Dynamic_Pages_Page_SEO_Tab_Title'
              : 'DYNAMIC_PAGES_PAGES_SEO_TAB_TITLE',
          ),
          url: await getPanelUrl({
            hostname,
            editorSdkProxy,
            relativeUrl: panelDefs.dynamicPageSeoPanelV2.url,
          }),
          helpId: HELP_ARTICLES.dynamicPages.seo,
        },
      ],
      [DYNAMIC_PAGE_VARIANT]: [],
    },
    pageDescriptors: {
      default: {
        icon: 'page_dynamic_icon',
      },
    },
  }
}
